import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { Badge } from "../../ui/badge";
import { PageHeader } from "./PageHeader";

/** One crumb: a label, and where it goes — omit `to` for the current page (rendered plain,
 *  not a link), matching the mockup's "Scenario Portfolio › Scenario › Scenario Monitor"
 *  trail where only earlier steps are clickable. */
export interface ScenarioCrumb {
  label: string;
  to?: string;
}

/**
 * The Simulator's own internal step header — the same eyebrow/title/subtitle
 * PageHeader already renders everywhere else in this product, plus the
 * persistent "SCENARIO · NOT REAL" badge on the two pages that need it
 * (Scenario Monitor/Snapshot).
 *
 * Used to also render its own breadcrumb trail (`crumbs`), one per step file. The stage
 * indicator now lives once, centrally, in ScenarioSimulatorPage.tsx instead — an embedded,
 * non-fixed StepsNavigation (SCENARIO_STEPS) sitting in the same spot this breadcrumb used to
 * render, giving the same shared numbered-step look as the product's other step trails without
 * pushing the 5 wizard pages into the outer product-wide nav (they read as one wizard's own
 * sub-steps, not five more top-level pages). `crumbs` is kept as an optional prop (unused by
 * any current caller) rather than removed outright, in case a future step ever needs a
 * sub-trail the shared step nav's flat list can't express.
 */
export function ScenarioHeader({
  crumbs = [],
  title,
  subtitle,
  notReal,
  right,
}: {
  crumbs?: ScenarioCrumb[];
  title: string;
  subtitle?: string;
  /** Shows the "SCENARIO · NOT REAL" badge — Scenario Monitor and Scenario Snapshot only. */
  notReal?: boolean;
  right?: ReactNode;
}) {
  return (
    <div>
      {crumbs.length > 1 && (
        <nav className="mb-2 flex flex-wrap items-center gap-1 text-xs">
          {crumbs.map((c, i) => (
            <span key={c.label} className="flex items-center gap-1">
              {i > 0 && <span aria-hidden className="text-muted-foreground">›</span>}
              {c.to ? (
                // Every earlier stage: Athena Green.
                <Link
                  to={c.to}
                  className="font-medium transition-opacity hover:opacity-80"
                  style={{ color: "var(--colorGreen)" }}
                >
                  {c.label}
                </Link>
              ) : (
                // The current stage: Athena Yellow.
                <span className="font-medium" style={{ color: "var(--colorYellow)" }}>
                  {c.label}
                </span>
              )}
            </span>
          ))}
        </nav>
      )}
      <PageHeader
        title={title}
        subtitle={subtitle}
        right={
          <>
            {notReal && (
              <Badge
                variant="outline"
                className="border-amber/60 text-[10px] uppercase tracking-[0.1em] text-amber"
              >
                Scenario · Not Real
              </Badge>
            )}
            {right}
          </>
        }
      />
    </div>
  );
}
